package link.erpango.quip.features.auth

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import link.erpango.quip.core.auth.IFirebaseUserWrapper
import link.erpango.quip.domain.usecases.IAuthUseCase
import link.erpango.quip.utils.DatetimeUtils

class AuthViewModel(
    private val authInteractor: IAuthUseCase
) : ViewModel() {
    var cont = 0
    val authUser = MutableStateFlow<IFirebaseUserWrapper?>(null)

    val authFlow = authInteractor.authFlow(viewModelScope.coroutineContext)
    val hasCredentials = MutableStateFlow<Boolean?>(null)

    private val _loginError = MutableStateFlow<String?>(null)
    val loginError = _loginError

    private val _isLoading = MutableStateFlow(false)
    val isLoading = _isLoading

    init {
        viewModelScope.launch {
            // Check initially
            checkStoredCredentials()
            
            // Observe changes
            authFlow.collect { user ->
                hasCredentials.value = user != null
            }
        }
    }


    fun signIn(email: String, pass: String) = viewModelScope.launch {
        _isLoading.value = true
        _loginError.value = null
        try {
            val success = authInteractor.signIn(email, pass)
            if (success) {
                checkCurrentSession()
            } else {
                _loginError.value = "Login failed"
            }
        } catch (e: Exception) {
            _loginError.value = e.message
        } finally {
            _isLoading.value = false
        }
    }


    suspend fun checkStoredCredentials(): Boolean {
        val hasCreds = authInteractor.getUserAuth() != null
        hasCredentials.value = hasCreds
        return hasCreds
    }

    suspend fun checkCredentialsAreValid(): Boolean {
        val userToken = authInteractor.getUserAuth()
        val tokenDurationInMs = 7L * 24L * 60L * 60L * 1000L // 7 days (access token lifetime)
        return ((userToken?.createdAt ?: return false) + tokenDurationInMs) > DatetimeUtils.currentTimeMillis()
    }

    fun checkCurrentSession() {

    }

    fun loginWithGoogle(idToken: String, onLoginSuccess: () -> Unit = {}) {
        viewModelScope.launch {
            _isLoading.value = true
            _loginError.value = null
            println("AuthViewModel: Starting loginWithGoogle with token length: ${idToken.length}")
            
            try {
                val auth = authInteractor.loginWithGoogle(idToken, this)
                if (auth != null) {
                    println("AuthViewModel: Backend verification success for user: ${auth.user}")
                    authInteractor.saveUserAuth(auth)
                    authInteractor.saveCurrentUser(auth)
                    println("AuthViewModel: Credentials saved locally")
                    
                    // Llamamos onLoginSuccess solo una vez, sin collect infinito
                    println("AuthViewModel: Triggering onLoginSuccess callback")
                    onLoginSuccess()
                } else {
                    println("AuthViewModel: Backend verification returned null (Unauthorized or Error)")
                    _loginError.value = "Login failed: Backend rejected the token."
                }
            } catch (e: Exception) {
                println("AuthViewModel: loginWithGoogle exception: ${e.message}")
                e.printStackTrace()
                _loginError.value = "Login Error: ${e.message}"
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun register(name: String, email: String, pass: String) = viewModelScope.launch {
        _isLoading.value = true
        _loginError.value = null
        try {
            val success = authInteractor.register(name, email, pass)
            if (success) {
                checkCurrentSession()
            } else {
                _loginError.value = "Register failed"
            }
        } catch (e: Exception) {
            _loginError.value = e.message
        } finally {
            _isLoading.value = false
        }
    }

    fun clearUserAuthData() {
        viewModelScope.launch {
            authInteractor.deleteAllTokens()
        }
    }
}